/** XeAgents.ai**/
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useQuery } from "@tanstack/react-query";
import { useAuthDialog } from "@/components/landing/AuthDialog";
import { usePilotRequestDialog } from "@/components/landing/PilotRequestDialog";
import { usePricingRegion } from "@/hooks/usePricingRegion";
import {
  PRICING_REGION_CURRENCY,
  formatPlanPrice,
  yearlySavingsPercent,
  type PricingCurrency,
  type PricingRegion,
} from "@shared/geo-pricing";

interface Plan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  country: string;
  currency: string;
  isActive: boolean;
  monthlyPrice: string;
  yearlyPrice: string | null;
  razorpayMonthlyPrice: string | null;
  razorpayYearlyPrice: string | null;
  maxAgents: number;
  maxCampaigns: number;
  maxContactsPerCampaign: number;
  maxWebhooks: number;
  maxKnowledgeBases: number;
  maxFlows: number;
  maxPhoneNumbers: number;
  maxSeats: number;
  includedCredits: number;
  canChooseLlm: boolean;
  canPurchaseNumbers: boolean;
  features: Record<string, boolean> | null;
}

/** What each price list is called in the currency switch. */
const REGION_LABELS: Record<PricingRegion, { label: string; symbol: string; code: PricingCurrency }> = {
  IN: { label: "India", symbol: "₹", code: "INR" },
  US: { label: "International", symbol: "$", code: "USD" },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.4, 0.25, 1] },
  },
};

function toNumber(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * What a plan costs, in its own currency.
 *
 * Each price list is a separate set of rows - the India rows quote INR in the
 * ordinary price columns - so a row's own numbers are authoritative. The
 * razorpay_* columns are only a fallback for older INR rows that left the
 * plain columns empty.
 */
function planPricing(plan: Plan, fallbackCurrency: PricingCurrency) {
  const currency: PricingCurrency =
    plan.currency === "INR" || plan.currency === "USD" ? plan.currency : fallbackCurrency;

  const monthly = toNumber(plan.monthlyPrice) ?? toNumber(plan.razorpayMonthlyPrice) ?? 0;
  const yearly = toNumber(plan.yearlyPrice) ?? toNumber(plan.razorpayYearlyPrice);

  return { currency, monthly, yearly };
}

/**
 * The same nine lines for every plan, so the cards read as one table rather
 * than four unrelated lists, and each line comes off the plan row instead of
 * being written out per tier.
 */
function planFeatures(plan: Plan): string[] {
  const flags = plan.features ?? {};
  const n = (value: number) => value.toLocaleString();

  const support = flags.dedicatedManager
    ? "Dedicated account manager"
    : flags.prioritySupport
      ? "Priority support"
      : flags.chatSupport
        ? "Chat and email support"
        : "Email support";

  return [
    `${n(plan.includedCredits)} calling credits included`,
    plan.maxAgents === 1 ? "1 AI agent" : `${n(plan.maxAgents)} AI agents`,
    plan.maxCampaigns === 1 ? "1 campaign" : `${n(plan.maxCampaigns)} campaigns`,
    `${n(plan.maxContactsPerCampaign)} contacts per campaign`,
    plan.canPurchaseNumbers
      ? `${n(plan.maxPhoneNumbers)} phone number${plan.maxPhoneNumbers === 1 ? "" : "s"}`
      : "System-assigned phone number",
    plan.maxSeats === 1 ? "Single user" : `Up to ${n(plan.maxSeats)} team members`,
    `${n(plan.maxFlows)} flow automations, ${n(plan.maxKnowledgeBases)} knowledge bases`,
    flags.whiteLabel ? "White-label branding" : support,
    plan.canChooseLlm ? "Choose your LLM model" : "Recordings and transcripts",
  ];
}

/** The plans table flags its own hero tier in the copy; the badge says it instead. */
const POPULAR_MARKER = /\s*most popular!?/i;

function isPopular(plan: Plan): boolean {
  return POPULAR_MARKER.test(plan.description ?? "");
}

function planDescription(plan: Plan): string {
  return (plan.description ?? "").replace(POPULAR_MARKER, "").trim();
}

const FeatureItem = ({ feature, index }: { feature: string; index: number }) => (
  <li className="flex items-start gap-2.5 text-sm" data-testid={`feature-item-${index}`}>
    <Check className="h-4 w-4 mt-0.5 text-violet-600 dark:text-violet-400 flex-shrink-0" />
    <span>{feature}</span>
  </li>
);

export function PricingSection() {
  const { openAuth } = useAuthDialog();
  const { open: openPilotRequest } = usePilotRequestDialog();
  const [isYearly, setIsYearly] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const { t } = useTranslation("landing");
  const { region, chooseRegion } = usePricingRegion();
  const otherRegion: PricingRegion = region === "IN" ? "US" : "IN";

  // A price list is a different set of rows, not a converted number, so
  // switching currency refetches rather than multiplying by a rate.
  const { data: plans, isLoading } = useQuery<Plan[]>({
    queryKey: ["/api/plans", { country: region, app: "xevoicepro" }],
    queryFn: async () => {
      const res = await fetch(`/api/plans?country=${region}&app=xevoicepro`);
      if (!res.ok) throw new Error("Failed to fetch plans");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const fallbackCurrency = PRICING_REGION_CURRENCY[region];

  // /api/plans returns retired rows too - India still carries several - so the
  // landing page shows only what is actually sellable today.
  const visiblePlans = (plans ?? [])
    .filter((plan) => plan.isActive !== false)
    .sort(
      (a, b) => planPricing(a, fallbackCurrency).monthly - planPricing(b, fallbackCurrency).monthly,
    );

  // Quote the discount the rows actually carry rather than a hardcoded "20%".
  const savingsPercent = visiblePlans.reduce((best, plan) => {
    const { monthly, yearly } = planPricing(plan, fallbackCurrency);
    return Math.max(best, yearlySavingsPercent(monthly, yearly));
  }, 0);

  return (
    <div className="py-12 sm:py-16 md:py-24 lg:py-28" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center space-y-3 sm:space-y-4 mb-8 sm:mb-10"
        >
          <h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold"
            data-testid="pricing-headline"
          >
            {t("pricing.titleA")}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-500 to-fuchsia-500">
              {t("pricing.titleB")}
            </span>
            {t("pricing.titleC")}
          </h2>
          <p
            className="text-sm sm:text-base md:text-lg text-muted-foreground"
            data-testid="pricing-subheadline"
          >
            {t("pricing.subtitle")}
          </p>
        </motion.div>

        <div className="flex items-center justify-center mb-10">
          <div className="flex items-center gap-3 bg-muted/50 rounded-full px-4 py-2">
            <span
              className={`text-sm font-medium transition-colors ${
                !isYearly ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {t("pricing.monthly")}
            </span>
            <Switch
              checked={isYearly}
              onCheckedChange={setIsYearly}
              data-testid="switch-billing-period"
            />
            <span
              className={`text-sm font-medium transition-colors ${
                isYearly ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {t("pricing.yearly")}
            </span>
            {isYearly && savingsPercent > 0 && (
              <Badge
                variant="secondary"
                className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                data-testid="badge-yearly-savings"
              >
                {t("pricing.save", { percent: savingsPercent })}
              </Badge>
            )}
          </div>

        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : visiblePlans.length === 0 ? (
          <div className="text-center space-y-4 py-10" data-testid="pricing-empty">
            <p className="text-muted-foreground">
              {t("pricing.updating", { code: REGION_LABELS[region].code })}
            </p>
            <Button
              variant="outline"
              onClick={() => openPilotRequest()}
              data-testid="button-pricing-contact"
            >
              {t("pricing.talkToSales")}
            </Button>
          </div>
        ) : (
          <motion.div
            variants={shouldReduceMotion ? {} : containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 items-stretch"
            data-testid="pricing-grid"
          >
            {visiblePlans.map((plan, index) => {
              const { currency, monthly, yearly } = planPricing(plan, fallbackCurrency);
              const popular = isPopular(plan);
              const isFree = monthly === 0;
              // A plan with no yearly row is simply twelve monthly payments.
              const amount = isYearly ? (yearly ?? monthly * 12) : monthly;
              const features = planFeatures(plan);

              return (
                <motion.div
                  key={plan.id}
                  variants={shouldReduceMotion ? {} : itemVariants}
                  data-testid={`pricing-card-${index}`}
                >
                  <Card
                    className={`p-5 sm:p-6 rounded-2xl hover-elevate transition-all h-full ${
                      popular
                        ? "relative border-2 border-violet-500/50 dark:border-violet-400/50 bg-gradient-to-br from-violet-50/60 to-purple-50/40 dark:from-violet-950/40 dark:to-purple-950/20 shadow-xl shadow-violet-500/10"
                        : ""
                    }`}
                  >
                    {popular && (
                      <Badge
                        className="absolute -top-3 right-6 bg-gradient-to-r from-violet-600 to-purple-600 text-white border-0 shadow-lg"
                        data-testid="popular-badge"
                      >
                        {t("pricing.popular")}
                      </Badge>
                    )}
                    <div className="flex flex-col h-full gap-5">
                      <div>
                        <h3
                          className="text-xl sm:text-2xl font-bold"
                          data-testid={`plan-name-${index}`}
                        >
                          {plan.displayName}
                        </h3>
                        <p
                          className="text-sm text-muted-foreground mt-2 min-h-[2.5rem]"
                          data-testid={`plan-description-${index}`}
                        >
                          {planDescription(plan)}
                        </p>
                      </div>

                      <div
                        className="text-3xl sm:text-4xl font-bold"
                        data-testid={`plan-price-${index}`}
                      >
                        {isFree ? (
                          <span>{t("pricing.free")}</span>
                        ) : (
                          <>
                            {formatPlanPrice(amount, currency)}
                            <span className="text-base font-normal text-muted-foreground">
                              {isYearly ? t("pricing.perYear") : t("pricing.perMonth")}
                            </span>
                          </>
                        )}
                      </div>

                      <ul className="space-y-3 flex-1" data-testid={`plan-features-${index}`}>
                        {features.map((feature, featureIndex) => (
                          <FeatureItem
                            key={featureIndex}
                            feature={feature}
                            index={featureIndex}
                          />
                        ))}
                      </ul>

                      <Button
                        variant={popular ? "default" : "outline"}
                        size="lg"
                        className={`w-full h-12 ${
                          popular
                            ? "bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white border-0 shadow-lg shadow-violet-600/30"
                            : "border-violet-200 dark:border-violet-800 hover:bg-violet-50 dark:hover:bg-violet-950/40"
                        }`}
                        onClick={() => openAuth("register")}
                        data-testid={`button-plan-${index}`}
                      >
                        {isFree ? t("pricing.startFree") : t("pricing.getStarted")}
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center text-sm text-muted-foreground mt-8 space-y-2"
        >
          <p>{t("pricing.billingNote")}</p>

          {/*
            Geo-detected, not geo-locked. India is quoted in INR and everywhere
            else in USD, so this is not a currency the visitor is meant to shop
            around in - it sits below the grid as one quiet line rather than
            beside the billing-period switch as a second primary control.

            It still has to be here: detection has no IP source in production
            (/api/geo/country reads a CDN header the ALB does not send), so it
            falls back to the browser's time zone. An Indian buyer on a laptop
            set to New York would otherwise have no way to reach the price they
            will actually be billed.
          */}
          <p>
            {t("pricing.pricesShownIn", { code: REGION_LABELS[region].code })}{" "}
            <button
              type="button"
              onClick={() => chooseRegion(otherRegion)}
              className="underline underline-offset-4 hover:text-foreground transition-colors"
              data-testid={`button-currency-${REGION_LABELS[otherRegion].code.toLowerCase()}`}
            >
              {region === "IN" ? t("pricing.outsideIndia") : t("pricing.inIndia")}{" "}
              {t("pricing.showPricing", { code: REGION_LABELS[otherRegion].code })}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export default PricingSection;
